import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { T10NS_QUERY_KEY, getT10ns } from '@/queries/t10ns';
import { AppContext } from '@/contexts/app-context';
import { dict } from './dict';

export function useLocalization() {
    const context = useContext(AppContext);

    const t10nsQuery = useQuery({
        queryKey: [T10NS_QUERY_KEY],
        queryFn: () => getT10ns(context),
        enabled: false,
    });

    const getLocale = () => context.locale;
    const getLanguage = () => context.language;
    const getCurrencyCode = () => context.currency;
    const getCountry = () => context.country;

    const localize = (key: string, useDict = false) => {
        if (useDict) {
            return dict?.[key]?.[getLanguage()] ?? `No translation for ${key}`;
        }

        if (!t10nsQuery.data) {
            return '';
        }

        return t10nsQuery?.data[key] ?? `No translation for ${key}`;
    };

    // Whether a translation key actually resolves to a value. `localize` falls back to a
    // "No translation for <key>" sentinel for missing keys, which is fine for keys that are
    // guaranteed to be filled in, but not for newly added ones — those stay absent from the
    // API until an editor fills them in per market. Use this to skip rendering optional copy
    // rather than leaking the sentinel to users.
    const hasTranslation = (key: string) => Boolean(t10nsQuery.data?.[key]);

    const getCurrencySymbol = () =>
        new Intl.NumberFormat(getLocale(), { style: 'currency', currency: context.currency }).format(0).replace(/[0-9.,]/g, '');

    const formatNumber = (nrToFormat: number | string, decimals = false) => {
        if (typeof nrToFormat !== 'number') {
            nrToFormat = parseFloat(`${nrToFormat}`?.replaceAll(/[^0-9]/g, '') || '');
        }

        return new Intl.NumberFormat(getLocale(), {
            style: 'currency',
            currency: context.currency,
            maximumFractionDigits: decimals ? 2 : 0,
        })
            .format(nrToFormat)
            .replace('kr.', 'kr');
    };

    return {
        localize,
        hasTranslation,
        getCurrencySymbol,
        getLocale,
        getLanguage,
        formatNumber,
        formatCurrency: formatNumber,
        getCurrencyCode,
        getCountry,
    };
}
